import type { Money } from './money.js';
import type { AccountTransactionClassification } from './statement-profile.js';

export const FUND_TRANSFER_STATUSES = ['requested', 'approved', 'completed', 'cancelled'] as const;
export type FundTransferStatus = (typeof FUND_TRANSFER_STATUSES)[number];

/** The two lines a transfer is completed with: the source's debit and the destination's credit. */
export const FUND_TRANSFER_SIDES = ['out', 'in'] as const;
export type FundTransferSide = (typeof FUND_TRANSFER_SIDES)[number];

/**
 * A transfer of the source organization's own funds moves money in both
 * ledgers; a pass-through forwards money that landed in the source's account
 * for the destination, and moves neither —
 * docs/decisions/0044-a-match-is-an-allocation-a-person-confirms.md §7.
 */
export const FUND_TRANSFER_SHAPES = ['own_funds', 'pass_through'] as const;
export type FundTransferShape = (typeof FUND_TRANSFER_SHAPES)[number];

export function fundTransferShape(transfer: {
  readonly sourceTransactionId: string | null;
}): FundTransferShape {
  return transfer.sourceTransactionId === null ? 'own_funds' : 'pass_through';
}

export type TransferLine = {
  readonly organizationId: string;
  readonly direction: 'credit' | 'debit';
  readonly classification: string;
  readonly excluded: boolean;
  readonly allocatedMinor: bigint;
  readonly amount: Money;
};

export type TransferTerms = {
  readonly fromOrganizationId: string;
  readonly toOrganizationId: string;
  readonly amount: Money;
  readonly shape: FundTransferShape;
};

export const FUND_TRANSFER_SOURCE_INELIGIBILITY = [
  'other_organization',
  'not_donor_payment',
  'excluded',
  'other_currency',
] as const;
export type FundTransferSourceIneligibility = (typeof FUND_TRANSFER_SOURCE_INELIGIBILITY)[number];

export type FundTransferSourceRefusal =
  | {
      readonly reason: 'FUND_TRANSFER_SOURCE_NOT_ELIGIBLE';
      readonly why: FundTransferSourceIneligibility;
    }
  | { readonly reason: 'FUND_TRANSFER_SOURCE_EXCEEDS_REMAINDER' };

/**
 * Whether a credit can be forwarded as a pass-through: a donor's payment on an
 * account of the source organization, not excluded, in the transfer's currency,
 * with at least the transfer's amount nobody has matched. The amount is taken
 * from its remainder, so a line that paid the source's own donations too can
 * still forward the part that was the destination's.
 */
export function passThroughSourceRefusal(
  transfer: Pick<TransferTerms, 'fromOrganizationId' | 'amount'>,
  line: TransferLine,
): FundTransferSourceRefusal | null {
  const ineligible = (why: FundTransferSourceIneligibility) =>
    ({ reason: 'FUND_TRANSFER_SOURCE_NOT_ELIGIBLE', why }) as const;
  if (line.organizationId !== transfer.fromOrganizationId) return ineligible('other_organization');
  if (line.direction !== 'credit' || line.classification !== 'donor_payment') {
    return ineligible('not_donor_payment');
  }
  if (line.excluded) return ineligible('excluded');
  if (line.amount.currency !== transfer.amount.currency) return ineligible('other_currency');
  return transfer.amount.amountMinor > line.amount.amountMinor - line.allocatedMinor
    ? { reason: 'FUND_TRANSFER_SOURCE_EXCEEDS_REMAINDER' }
    : null;
}

export const FUND_TRANSFER_LINE_INELIGIBILITY = [
  'other_organization',
  'wrong_direction',
  'excluded',
  'classification',
] as const;
export type FundTransferLineIneligibility = (typeof FUND_TRANSFER_LINE_INELIGIBILITY)[number];

export type FundTransferLineRefusal =
  | {
      readonly reason: 'FUND_TRANSFER_LINE_NOT_ELIGIBLE';
      readonly why: FundTransferLineIneligibility;
    }
  | { readonly reason: 'FUND_TRANSFER_LINE_AMOUNT_MISMATCH' }
  | { readonly reason: 'FUND_TRANSFER_LINE_ALLOCATED' };

/**
 * Whether naming this line completes one side. A pass-through's incoming credit
 * is the only line a transfer names without allocating: it is the destination's
 * donor payment, matched to its donation there as an ordinary collection.
 */
export function transferAllocates(shape: FundTransferShape, side: FundTransferSide): boolean {
  return side === 'out' || shape === 'own_funds';
}

/** The classifications a side's line may carry. */
export function transferLineClassifications(
  shape: FundTransferShape,
  side: FundTransferSide,
): readonly AccountTransactionClassification[] {
  if (side === 'out') return ['internal_transfer', 'other'];
  return shape === 'pass_through'
    ? ['donor_payment']
    : ['donor_payment', 'internal_transfer', 'other'];
}

/**
 * Whether a line carried one side of the transfer: the source organization's
 * debit, or the destination's credit, for exactly the transfer's amount and
 * currency, not excluded, of a classification that side may carry — and, where
 * the transfer allocates it, holding nothing yet.
 */
export function transferLineRefusal(
  transfer: TransferTerms,
  side: FundTransferSide,
  line: TransferLine,
): FundTransferLineRefusal | null {
  const ineligible = (why: FundTransferLineIneligibility) =>
    ({ reason: 'FUND_TRANSFER_LINE_NOT_ELIGIBLE', why }) as const;
  const organizationId = side === 'out' ? transfer.fromOrganizationId : transfer.toOrganizationId;
  if (line.organizationId !== organizationId) return ineligible('other_organization');
  if (line.direction !== (side === 'out' ? 'debit' : 'credit'))
    return ineligible('wrong_direction');
  if (line.excluded) return ineligible('excluded');
  if (
    !(transferLineClassifications(transfer.shape, side) as readonly string[]).includes(
      line.classification,
    )
  ) {
    return ineligible('classification');
  }
  if (
    line.amount.currency !== transfer.amount.currency ||
    line.amount.amountMinor !== transfer.amount.amountMinor
  ) {
    return { reason: 'FUND_TRANSFER_LINE_AMOUNT_MISMATCH' };
  }
  return transferAllocates(transfer.shape, side) && line.allocatedMinor > 0n
    ? { reason: 'FUND_TRANSFER_LINE_ALLOCATED' }
    : null;
}
